import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import useApi from "../../components/Utils/api";
import toast from "react-hot-toast";

const CRON_TIMES_KEY = "settlement.cron.times";
const DATE_OFFSET_KEY = "settlement.date.offset.days";

function SettlementSchedule({ embedded = false, tabsSlot = null } = {}) {
  const api = useApi();

  const [timeSlots, setTimeSlots] = useState([]);
  const [newTime, setNewTime] = useState("");
  const [dateOffset, setDateOffset] = useState("1");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("system-config/")
      .then((response) => {
        const configs = response.data;
        const timesEntry = configs.find((c) => c.name === CRON_TIMES_KEY);
        const offsetEntry = configs.find((c) => c.name === DATE_OFFSET_KEY);

        if (timesEntry && timesEntry.value) {
          setTimeSlots(
            timesEntry.value.split(",").map((t) => t.trim()).filter(Boolean)
          );
        }
        if (offsetEntry && offsetEntry.value) {
          setDateOffset(offsetEntry.value);
        }
      })
      .catch(() => {
        toast.error("Failed to load settlement configuration");
      });
  }, []);

  const addTimeSlot = () => {
    if (!newTime) return;
    if (!/^\d{2}:\d{2}$/.test(newTime)) {
      toast.error("Enter a valid time in HH:MM format");
      return;
    }
    if (timeSlots.includes(newTime)) {
      toast.error("This time slot is already added");
      return;
    }
    setTimeSlots((prev) => [...prev, newTime].sort());
    setNewTime("");
  };

  const removeTimeSlot = (slot) => {
    setTimeSlots((prev) => prev.filter((t) => t !== slot));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`system-config/${CRON_TIMES_KEY}`, { value: timeSlots.join(",") });
      await api.put(`system-config/${DATE_OFFSET_KEY}`, { value: dateOffset });
      toast.success("Settlement schedule saved. Changes take effect within 30 minutes.");
    } catch (error) {
      toast.error("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const content = (
      <MDBox mt={3} mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <MDTypography variant="h5" fontWeight="medium" mb={2}>
              Settlement
            </MDTypography>
          </Grid>
          {tabsSlot && (
            <Grid item xs={12}>
              {tabsSlot}
            </Grid>
          )}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h5" fontWeight="medium" mb={1}>
                  Botim Settlement Schedule
                </MDTypography>
                <MDTypography variant="body2" color="text" mb={3}>
                  Configure the times at which the settlement service will download and process
                  the Botim Money daily statement. Changes are picked up automatically within
                  30 minutes.
                </MDTypography>

                <Divider />

                {/* Cron time slots */}
                <MDBox mt={3} mb={2}>
                  <MDTypography variant="h6" mb={1}>Run Times (HH:MM, server timezone)</MDTypography>

                  <MDBox display="flex" flexWrap="wrap" gap={1} mb={2} minHeight="40px">
                    {timeSlots.length === 0 && (
                      <MDTypography variant="body2" color="text">
                        No time slots configured.
                      </MDTypography>
                    )}
                    {timeSlots.map((slot) => (
                      <Chip
                        key={slot}
                        label={slot}
                        onDelete={() => removeTimeSlot(slot)}
                        color="info"
                        variant="outlined"
                      />
                    ))}
                  </MDBox>

                  <MDBox display="flex" gap={2} alignItems="center">
                    <TextField
                      label="Add time (HH:MM)"
                      value={newTime}
                      onChange={(e) => setNewTime(e.target.value)}
                      placeholder="09:00"
                      inputProps={{ maxLength: 5 }}
                      size="small"
                      sx={{ width: 160 }}
                    />
                    <MDButton color="info" size="small" onClick={addTimeSlot}>
                      Add
                    </MDButton>
                  </MDBox>
                </MDBox>

                <Divider />

                {/* Date offset */}
                <MDBox mt={3} mb={3}>
                  <MDTypography variant="h6" mb={1}>Statement Date Offset (days back)</MDTypography>
                  <MDTypography variant="body2" color="text" mb={2}>
                    1 means download yesterday&apos;s statement. Increase if the processor
                    publishes statements with a delay.
                  </MDTypography>
                  <TextField
                    label="Days back"
                    type="number"
                    value={dateOffset}
                    onChange={(e) => setDateOffset(e.target.value)}
                    inputProps={{ min: 1, max: 7 }}
                    size="small"
                    sx={{ width: 120 }}
                  />
                </MDBox>

                <MDButton
                  color="success"
                  variant="contained"
                  onClick={save}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Configuration"}
                </MDButton>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
  );

  // embedded=true renders content only (used as a tab inside layouts/settlement)
  if (embedded) return content;

  return (
    <DashboardLayout>
      {content}
      <Footer />
    </DashboardLayout>
  );
}

SettlementSchedule.propTypes = {
  // eslint-disable-next-line react/require-default-props
  embedded: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  tabsSlot: PropTypes.node,
};

export default SettlementSchedule;
